const MAX_SAMPLES = 300_000;
const HISTOGRAM_BINS = 64;
const round = (value, digits = 4) => Number(value.toFixed(digits));
function percentile(histogram, total, target) {
    const threshold = total * target;
    let cumulative = 0;
    for (let index = 0; index < histogram.length; index++) {
        cumulative += histogram[index] ?? 0;
        if (cumulative >= threshold)
            return index;
    }
    return histogram.length - 1;
}
export class ExposureAnalyzer {
    id = 'exposure';
    label = 'Exposure';
    analyze(frame, context) {
        const { width, height, data } = frame;
        const stride = Math.max(1, Math.ceil(Math.sqrt((width * height) / MAX_SAMPLES)));
        const histogram = new Array(256).fill(0);
        let total = 0;
        let lumaSum = 0;
        let highlightClips = 0;
        let shadowClips = 0;
        for (let y = 0; y < height; y += stride) {
            for (let x = 0; x < width; x += stride) {
                const offset = (y * width + x) * 4;
                const r = data[offset] ?? 0;
                const g = data[offset + 1] ?? 0;
                const b = data[offset + 2] ?? 0;
                const value = Math.max(0, Math.min(255, Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b)));
                histogram[value] = (histogram[value] ?? 0) + 1;
                total++;
                lumaSum += value;
                if (value >= 250)
                    highlightClips++;
                if (value <= 5)
                    shadowClips++;
            }
        }
        const averageLuma = lumaSum / Math.max(1, total);
        const highlightClipPercentage = highlightClips / Math.max(1, total) * 100;
        const shadowClipPercentage = shadowClips / Math.max(1, total) * 100;
        const lowPercentile = percentile(histogram, total, 0.05);
        const highPercentile = percentile(histogram, total, 0.95);
        const contrastScore = (highPercentile - lowPercentile) / 255;
        let exposureStatus = 'good';
        if (averageLuma < 68 || shadowClipPercentage > 25)
            exposureStatus = 'underexposed';
        else if (averageLuma > 192 || highlightClipPercentage > 15)
            exposureStatus = 'overexposed';
        else if (contrastScore > 0.8)
            exposureStatus = 'too_contrasty';
        else if (contrastScore < 0.14)
            exposureStatus = 'too_flat';
        const warnings = [];
        const addWarning = (code, message) => {
            warnings.push({ code, severity: 'warning', message, module_id: this.id, frame_number: context.frameNumber });
        };
        if (highlightClipPercentage >= 1)
            addWarning('highlights_clipping', 'Highlights are clipping');
        if (shadowClipPercentage >= 1)
            addWarning('blacks_crushed', 'Blacks are crushed');
        if (averageLuma < 68)
            addWarning('image_underexposed', 'Image appears underexposed');
        if (averageLuma > 192)
            addWarning('image_overexposed', 'Image appears overexposed');
        if (contrastScore < 0.14)
            addWarning('low_contrast', 'Low contrast may reduce edge confidence');
        const compactHistogram = Array.from({ length: HISTOGRAM_BINS }, (_, index) => {
            const start = index * 4;
            return histogram.slice(start, start + 4).reduce((sum, count) => sum + count, 0);
        });
        const histogramPeak = Math.max(1, ...compactHistogram);
        const normalizedHistogram = compactHistogram.map((count) => round(count / histogramPeak));
        const confidence = Math.min(1, 0.7 + Math.min(0.3, total / 100_000));
        return {
            module_id: this.id,
            module_name: this.label,
            confidence: round(confidence),
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            metrics: {
                average_luma: round(averageLuma, 2),
                highlight_clip_percentage: round(highlightClipPercentage, 2),
                shadow_clip_percentage: round(shadowClipPercentage, 2),
                contrast_score: round(contrastScore),
                exposure_status: exposureStatus,
                luminance_histogram: normalizedHistogram,
            },
            warnings,
            overlays: [],
        };
    }
}
//# sourceMappingURL=ExposureAnalyzer.js.map